import { useState, useEffect } from 'react';
import {
  Copy,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Filter,
  Search,
  Eye,
  RefreshCw,
  FileText,
  ArrowUpDown
} from 'lucide-react';
import {
  duplicateDetectionService,
  DuplicateGroup,
  DuplicateFilters,
  Project,
  Contract
} from '../services/duplicateDetectionService';
import { supabase } from '../lib/supabase';

export function DuplicateManager() {
  const [loading, setLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [recordType, setRecordType] = useState<'both' | 'project' | 'contract'>('both');
  const [viewMode, setViewMode] = useState<'duplicates' | 'all'>('duplicates');
  const [allRecords, setAllRecords] = useState<(Project | Contract)[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(20);
  const [sortField, setSortField] = useState<'name' | 'value' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [filters, setFilters] = useState<DuplicateFilters>({
    matchByName: true,
    matchByValue: true,
    matchByContact: false,
    valueTolerance: 0,
    nameMatchMode: 'exact'
  });

  const [stats, setStats] = useState({
    totalGroups: 0,
    totalDuplicates: 0,
    selectedForDeletion: 0
  });

  useEffect(() => {
    setStats({
      totalGroups: duplicateGroups.length,
      totalDuplicates: duplicateGroups.reduce((sum, g) => sum + g.records.length - 1, 0),
      selectedForDeletion: selectedRecords.size
    });
  }, [duplicateGroups, selectedRecords]);

  const loadAllRecords = async () => {
    setLoading(true);
    try {
      const records: (Project | Contract)[] = [];

      if (recordType === 'both' || recordType === 'project') {
        const { data: projects, error: projError } = await supabase
          .from('projects')
          .select('*')
          .order('created_at', { ascending: false });

        if (projError) throw projError;
        if (projects) records.push(...projects);
      }

      if (recordType === 'both' || recordType === 'contract') {
        const { data: contracts, error: contrError } = await supabase
          .from('financial_terms')
          .select(`
            *,
            customers (
              id,
              full_name,
              cpf,
              phone,
              email
            )
          `)
          .order('created_at', { ascending: false });

        if (contrError) throw contrError;
        if (contracts) records.push(...contracts as Contract[]);
      }

      setAllRecords(records);
      setSelectedRecords(new Set());
      setCurrentPage(1);
    } catch (error) {
      console.error('Error loading records:', error);
      alert('Erro ao carregar registros. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const detectDuplicates = async () => {
    setLoading(true);
    try {
      const groups: DuplicateGroup[] = [];

      if (recordType === 'both' || recordType === 'project') {
        const projectGroups = await duplicateDetectionService.detectProjectDuplicates(filters);
        groups.push(...projectGroups);
      }

      if (recordType === 'both' || recordType === 'contract') {
        const contractGroups = await duplicateDetectionService.detectContractDuplicates(filters);
        groups.push(...contractGroups);
      }

      setDuplicateGroups(groups);
      setSelectedGroups(new Set());
      setSelectedRecords(new Set());
    } catch (error) {
      console.error('Error detecting duplicates:', error);
      alert('Erro ao detectar duplicatas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroupSelection = (groupId: string) => {
    const newSelected = new Set(selectedGroups);
    const group = duplicateGroups.find(g => g.id === groupId);

    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
      if (group) {
        // Remove all records except one (keep at least one)
        group.records.slice(1).forEach(r => selectedRecords.delete(r.id));
      }
    } else {
      newSelected.add(groupId);
      if (group) {
        // Select all except the first one by default (but user can change)
        group.records.slice(1).forEach(r => selectedRecords.add(r.id));
      }
    }

    setSelectedGroups(newSelected);
    setSelectedRecords(new Set(selectedRecords));
  };

  const toggleRecordSelection = (recordId: string) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const toggleSingleRecordSelection = (recordId: string) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const getRecordType = (record: any): 'project' | 'contract' => {
    return record.customers ? 'contract' : 'project';
  };

  const handleSort = (field: 'name' | 'value' | 'date') => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getSortedRecords = (records: (Project | Contract)[]) => {
    const sorted = [...records];

    sorted.sort((a, b) => {
      let compareA: any;
      let compareB: any;

      switch (sortField) {
        case 'name':
          const typeA = getRecordType(a);
          const typeB = getRecordType(b);
          compareA = getRecordName(a, typeA).toLowerCase();
          compareB = getRecordName(b, typeB).toLowerCase();
          break;
        case 'value':
          const typeValA = getRecordType(a);
          const typeValB = getRecordType(b);
          compareA = getRecordValue(a, typeValA);
          compareB = getRecordValue(b, typeValB);
          break;
        case 'date':
          compareA = new Date(a.created_at).getTime();
          compareB = new Date(b.created_at).getTime();
          break;
        default:
          return 0;
      }

      if (typeof compareA === 'string' && typeof compareB === 'string') {
        return sortOrder === 'asc'
          ? compareA.localeCompare(compareB)
          : compareB.localeCompare(compareA);
      }

      return sortOrder === 'asc' ? compareA - compareB : compareB - compareA;
    });

    return sorted;
  };

  const confirmDeletion = () => {
    if (selectedRecords.size === 0) {
      alert('Nenhum registro selecionado para exclusão.');
      return;
    }

    if (viewMode === 'duplicates') {
      // Validate that at least one record is kept in each group
      const invalidGroups: string[] = [];
      duplicateGroups.forEach(group => {
        const selectedInGroup = group.records.filter(r => selectedRecords.has(r.id)).length;
        if (selectedInGroup === group.records.length) {
          invalidGroups.push(getRecordName(group.records[0], group.type));
        }
      });

      if (invalidGroups.length > 0) {
        alert(
          `⚠️ ATENÇÃO: Você não pode excluir TODOS os registros de um grupo!\n\n` +
          `Pelo menos 1 registro deve ser mantido em cada grupo duplicado.\n\n` +
          `Grupos com problema:\n${invalidGroups.map(name => `• ${name}`).join('\n')}`
        );
        return;
      }
    }

    setShowConfirmModal(true);
  };

  const executeDeletion = async () => {
    setShowConfirmModal(false);
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const projectIds: string[] = [];
      const contractIds: string[] = [];

      if (viewMode === 'duplicates') {
        duplicateGroups.forEach(group => {
          group.records.forEach(record => {
            if (selectedRecords.has(record.id)) {
              if (group.type === 'project') {
                projectIds.push(record.id);
              } else {
                contractIds.push(record.id);
              }
            }
          });
        });
      } else {
        // All records mode
        allRecords.forEach(record => {
          if (selectedRecords.has(record.id)) {
            const type = getRecordType(record);
            if (type === 'project') {
              projectIds.push(record.id);
            } else {
              contractIds.push(record.id);
            }
          }
        });
      }

      let totalDeleted = 0;

      if (projectIds.length > 0) {
        const result = await duplicateDetectionService.deleteDuplicates(
          projectIds,
          'project',
          viewMode === 'all' ? 'Exclusão manual via interface' : 'Exclusão de duplicatas via interface',
          user.id
        );
        totalDeleted += result.deletedCount;
      }

      if (contractIds.length > 0) {
        const result = await duplicateDetectionService.deleteDuplicates(
          contractIds,
          'contract',
          viewMode === 'all' ? 'Exclusão manual via interface' : 'Exclusão de duplicatas via interface',
          user.id
        );
        totalDeleted += result.deletedCount;
      }

      // Limpa seleções antes de recarregar
      setSelectedRecords(new Set());
      setSelectedGroups(new Set());

      // Recarrega os dados sem redirecionar
      if (viewMode === 'duplicates') {
        await detectDuplicates();
      } else {
        await loadAllRecords();
      }

      // Mostra mensagem de sucesso em banner (não bloqueia)
      setSuccessMessage(`✅ ${totalDeleted} registro(s) excluído(s) com sucesso! Você pode continuar deletando.`);

      // Remove mensagem após 5 segundos
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);

    } catch (error) {
      console.error('Error deleting records:', error);
      alert('Erro ao excluir registros. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    try {
      const logs = await duplicateDetectionService.getAuditLogs(50);
      setAuditLogs(logs);
      setShowAuditLogs(true);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      alert('Erro ao carregar logs de auditoria.');
    }
  };

  const formatValue = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getRecordName = (record: Project | Contract, type: 'project' | 'contract'): string => {
    if (type === 'project') {
      return (record as Project).name;
    } else {
      return (record as Contract).customers?.full_name || 'N/A';
    }
  };

  const getRecordValue = (record: Project | Contract, type: 'project' | 'contract'): number => {
    if (type === 'project') {
      return (record as Project).value;
    } else {
      return (record as Contract).total_amount;
    }
  };

  const SortHeader = ({ field, label, align = 'left' }: { field: 'name' | 'value' | 'date'; label: string; align?: 'left' | 'right' }) => (
    <th
      onClick={() => handleSort(field)}
      className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none`}
    >
      <div className={`flex items-center gap-2 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <ArrowUpDown
          className={`w-4 h-4 transition-all ${
            sortField === field
              ? 'text-blue-600 scale-110'
              : 'text-gray-400'
          } ${
            sortField === field && sortOrder === 'desc' ? 'rotate-180' : ''
          }`}
        />
      </div>
    </th>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {successMessage && (
        <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center gap-3 animate-fade-in shadow-lg">
          <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0" />
          <p className="text-green-800 font-medium flex-1">{successMessage}</p>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800 transition-colors"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Copy className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gerenciador de Duplicatas</h1>
              <p className="text-sm text-gray-600">
                Identifique e remova registros duplicados de projetos e contratos
              </p>
            </div>
          </div>
          <button
            onClick={loadAuditLogs}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            <FileText className="w-4 h-4" />
            Logs de Auditoria
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Grupos Encontrados</p>
                <p className="text-2xl font-bold text-blue-900">{stats.totalGroups}</p>
              </div>
              <Copy className="w-8 h-8 text-blue-400" />
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Total de Duplicatas</p>
                <p className="text-2xl font-bold text-orange-900">{stats.totalDuplicates}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-400" />
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Selecionados p/ Exclusão</p>
                <p className="text-2xl font-bold text-red-900">{stats.selectedForDeletion}</p>
              </div>
              <Trash2 className="w-8 h-8 text-red-400" />
            </div>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => {
                setViewMode('duplicates');
                setSelectedRecords(new Set());
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'duplicates'
                  ? 'bg-blue-600 text-white shadow'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              Modo Duplicatas
            </button>
            <button
              onClick={() => {
                setViewMode('all');
                setSelectedRecords(new Set());
                loadAllRecords();
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-green-600 text-white shadow'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              Mostrar Todos
            </button>
          </div>

          <div className="flex items-center gap-3">
            {viewMode === 'duplicates' && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
              </button>
            )}

            {viewMode === 'duplicates' ? (
              <button
                onClick={detectDuplicates}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Buscar Duplicatas
              </button>
            ) : (
              <button
                onClick={loadAllRecords}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Atualizar Lista
              </button>
            )}

            {stats.selectedForDeletion > 0 && (
              <button
                onClick={confirmDeletion}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ml-auto"
              >
                <Trash2 className="w-4 h-4" />
                Excluir {stats.selectedForDeletion} Selecionados
              </button>
            )}
          </div>
        </div>

        {viewMode === 'duplicates' && (
          <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Como Funciona - Seleção Manual Completa
            </h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>✓ <strong>TODOS os registros</strong> podem ser selecionados manualmente para exclusão</li>
              <li>✓ O primeiro registro é <strong>SUGERIDO</strong> para manter, mas você pode alterá-lo</li>
              <li>✓ Marque os checkboxes dos registros que deseja <strong>EXCLUIR</strong></li>
              <li>⚠️ <strong>Obrigatório:</strong> Pelo menos 1 registro deve ser mantido em cada grupo</li>
              <li>📝 Todos os registros excluídos ficam salvos nos Logs de Auditoria</li>
            </ul>
          </div>
        )}

        {viewMode === 'all' && (
          <div className="bg-yellow-50 rounded-lg p-4 mb-6 border border-yellow-200">
            <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Sobre as Datas dos Registros
            </h3>
            <div className="text-sm text-yellow-800 space-y-2">
              <p>
                <strong>⚠️ Importante:</strong> Se você vê muitos registros com a mesma data (ex: 14/01/2026),
                isso ocorre porque os projetos foram importados sem uma data de venda definida.
              </p>
              <p>
                <strong>Como funciona:</strong>
              </p>
              <ul className="ml-4 space-y-1">
                <li>• <strong>Contratos:</strong> Sempre usam a data de criação no sistema</li>
                <li>• <strong>Projetos:</strong> Usam a "Data de Venda" se disponível, caso contrário usam a data de criação</li>
                <li>• Projetos importados geralmente não têm "Data de Venda" definida</li>
              </ul>
              <p className="mt-2">
                <strong>Solução:</strong> Você pode editar as datas de venda dos projetos individualmente
                na tela de "Relatórios de Vendas" clicando no ícone de edição ao lado da data.
              </p>
            </div>
          </div>
        )}

        {viewMode === 'duplicates' && showFilters && (
          <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filtros de Detecção
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Registro
                </label>
                <select
                  value={recordType}
                  onChange={(e) => setRecordType(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="both">Ambos (Projetos e Contratos)</option>
                  <option value="project">Apenas Projetos</option>
                  <option value="contract">Apenas Contratos</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modo de Comparação de Nome
                </label>
                <select
                  value={filters.nameMatchMode}
                  onChange={(e) => setFilters({ ...filters, nameMatchMode: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="exact">Exato (100%)</option>
                  <option value="similar">Similar (≥80%)</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.matchByName}
                    onChange={(e) => setFilters({ ...filters, matchByName: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Comparar por Nome/Cliente</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.matchByValue}
                    onChange={(e) => setFilters({ ...filters, matchByValue: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Comparar por Valor</span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={filters.matchByContact}
                    onChange={(e) => setFilters({ ...filters, matchByContact: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Comparar por Contato</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tolerância de Valor (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.valueTolerance}
                  onChange={(e) => setFilters({ ...filters, valueTolerance: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  0% = valores idênticos, 5% = permite diferença de até 5%
                </p>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
            <p className="text-gray-600">Detectando duplicatas...</p>
          </div>
        )}

        {!loading && viewMode === 'duplicates' && duplicateGroups.length === 0 && (
          <div className="text-center py-12 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-600" />
            <p className="text-gray-900 font-medium">Nenhuma duplicata encontrada!</p>
            <p className="text-sm text-gray-600 mt-1">
              Clique em "Buscar Duplicatas" para começar a análise
            </p>
          </div>
        )}

        {!loading && viewMode === 'all' && allRecords.length === 0 && (
          <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
            <FileText className="w-12 h-12 mx-auto mb-4 text-blue-600" />
            <p className="text-gray-900 font-medium">Nenhum registro encontrado!</p>
            <p className="text-sm text-gray-600 mt-1">
              Clique em "Atualizar Lista" para carregar os registros
            </p>
          </div>
        )}

        {!loading && viewMode === 'all' && allRecords.length > 0 && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">
                  <strong>Total de registros:</strong> {allRecords.length} |
                  <strong className="ml-3">Página:</strong> {currentPage} de {Math.ceil(allRecords.length / recordsPerPage)}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <ArrowUpDown className="w-4 h-4" />
                  <span>
                    Ordenado por: <strong className="text-blue-600">
                      {sortField === 'name' ? 'Nome' : sortField === 'value' ? 'Valor' : 'Data'}
                    </strong>
                    {' '}({sortOrder === 'asc' ? 'A-Z / Menor-Maior / Antiga-Recente' : 'Z-A / Maior-Menor / Recente-Antiga'})
                  </span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            const pageStart = (currentPage - 1) * recordsPerPage;
                            const pageEnd = pageStart + recordsPerPage;
                            const sortedRecords = getSortedRecords(allRecords);
                            const pageRecords = sortedRecords.slice(pageStart, pageEnd);
                            setSelectedRecords(new Set(pageRecords.map(r => r.id)));
                          } else {
                            setSelectedRecords(new Set());
                          }
                        }}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <SortHeader field="name" label="Nome" />
                    <SortHeader field="value" label="Valor" align="right" />
                    <SortHeader field="date" label="Data" />
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contato</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getSortedRecords(allRecords)
                    .slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage)
                    .map((record) => {
                      const type = getRecordType(record);
                      const isSelected = selectedRecords.has(record.id);
                      return (
                        <tr key={record.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-red-50' : ''}`}>
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSingleRecordSelection(record.id)}
                              className="w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                            />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              type === 'project'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {type === 'project' ? 'Projeto' : 'Contrato'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {getRecordName(record, type)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 text-right font-medium">
                            {formatValue(getRecordValue(record, type))}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">
                            {formatDate(record.created_at)}
                            {type === 'project' && (record as Project).sale_date && (
                              <div className="text-xs text-green-600">
                                Venda: {formatDate((record as Project).sale_date!)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {type === 'project' ? (record as Project).contact || 'N/A' : (record as Contract).customers?.phone || 'N/A'}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => {
                                if (window.confirm(`Tem certeza que deseja excluir este registro?\n\nNome: ${getRecordName(record, type)}\nValor: ${formatValue(getRecordValue(record, type))}`)) {
                                  setSelectedRecords(new Set([record.id]));
                                  setTimeout(() => confirmDeletion(), 100);
                                }
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                            >
                              <Trash2 className="w-4 h-4" />
                              Deletar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700">
                Página {currentPage} de {Math.ceil(allRecords.length / recordsPerPage)}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(Math.ceil(allRecords.length / recordsPerPage), currentPage + 1))}
                disabled={currentPage >= Math.ceil(allRecords.length / recordsPerPage)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Próxima
              </button>
            </div>
          </div>
        )}

        {!loading && viewMode === 'duplicates' && duplicateGroups.length > 0 && (
          <div className="space-y-4">
            {duplicateGroups.map((group) => (
              <div
                key={group.id}
                className="bg-white border-2 border-orange-200 rounded-lg overflow-hidden"
              >
                <div className="bg-orange-50 px-4 py-3 flex items-center justify-between border-b border-orange-200">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(group.id)}
                      onChange={() => toggleGroupSelection(group.id)}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Grupo de {group.records.length} registros duplicados
                      </p>
                      <p className="text-sm text-gray-600">
                        Tipo: {group.type === 'project' ? 'Projetos' : 'Contratos'} |
                        Razão: {group.reason} |
                        Similaridade: {(group.matchScore * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Ver Detalhes
                  </button>
                </div>

                <div className="p-4">
                  <div className="grid gap-3">
                    {group.records.map((record, index) => (
                      <div
                        key={record.id}
                        className={`p-4 rounded-lg border-2 ${
                          selectedRecords.has(record.id)
                            ? 'bg-red-50 border-red-300'
                            : index === 0
                            ? 'bg-blue-50 border-blue-300'
                            : 'bg-gray-50 border-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3 flex-1">
                            <input
                              type="checkbox"
                              checked={selectedRecords.has(record.id)}
                              onChange={() => toggleRecordSelection(record.id)}
                              className="w-5 h-5 text-red-600 rounded focus:ring-2 focus:ring-red-500 mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {index === 0 && !selectedRecords.has(record.id) && (
                                  <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded">
                                    SUGERIDO MANTER (pode alterar)
                                  </span>
                                )}
                                {selectedRecords.has(record.id) && (
                                  <span className="px-2 py-1 bg-red-600 text-white text-xs font-semibold rounded">
                                    SERÁ EXCLUÍDO
                                  </span>
                                )}
                                {!selectedRecords.has(record.id) && index > 0 && (
                                  <span className="px-2 py-1 bg-green-600 text-white text-xs font-semibold rounded">
                                    SERÁ MANTIDO
                                  </span>
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <p className="text-gray-600 font-medium">Nome:</p>
                                  <p className="text-gray-900">{getRecordName(record, group.type)}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-medium">Valor:</p>
                                  <p className="text-gray-900">{formatValue(getRecordValue(record, group.type))}</p>
                                </div>
                                <div>
                                  <p className="text-gray-600 font-medium">Data:</p>
                                  <p className="text-gray-900">{formatDate(record.created_at)}</p>
                                </div>
                                {group.type === 'project' && (
                                  <div>
                                    <p className="text-gray-600 font-medium">Contato:</p>
                                    <p className="text-gray-900">{(record as Project).contact || 'N/A'}</p>
                                  </div>
                                )}
                                {group.type === 'contract' && (
                                  <>
                                    <div>
                                      <p className="text-gray-600 font-medium">CPF:</p>
                                      <p className="text-gray-900">{(record as Contract).customers?.cpf || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p className="text-gray-600 font-medium">Telefone:</p>
                                      <p className="text-gray-900">{(record as Contract).customers?.phone || 'N/A'}</p>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <h3 className="text-xl font-bold text-gray-900">Confirmar Exclusão</h3>
            </div>

            <p className="text-gray-700 mb-4">
              Você está prestes a excluir <strong>{stats.selectedForDeletion} registros</strong> duplicados.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Atenção:</strong> Esta ação não pode ser desfeita. Os registros serão
                permanentemente removidos do sistema, mas ficarão registrados nos logs de auditoria.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={executeDeletion}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}

      {showAuditLogs && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
            <div className="bg-gray-800 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6" />
                <h3 className="text-xl font-bold">Logs de Auditoria</h3>
              </div>
              <button
                onClick={() => setShowAuditLogs(false)}
                className="text-white hover:text-gray-300"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {auditLogs.length === 0 ? (
                <p className="text-center text-gray-600 py-8">Nenhum log de exclusão encontrado.</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            log.record_type === 'project'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {log.record_type === 'project' ? 'PROJETO' : 'CONTRATO'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{formatDate(log.created_at)}</p>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">
                        <strong>Razão:</strong> {log.reason}
                      </p>
                      <p className="text-xs text-gray-600">
                        <strong>ID:</strong> {log.record_id}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
